import { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Handlebars from 'handlebars';
import { Block } from 'apps/ng/src/app/services';
import { registerHandlebarsHelpers } from 'apps/ng/src/app/helpers/handlebars.helpers';

interface BlockPreviewProps {
  blocks: Block[];
  htmlCode: string;
  cssCode: string;
  jsCode: string;
  variables?: string;
  context?: any;
  hideHeader?: boolean;
  disableScripts?: boolean;
}

const StyledPreviewContainer = styled.div<{ $hideHeader: boolean }>`
  width: 100%;
  height: ${({ $hideHeader }) => ($hideHeader ? '100%' : 'auto')};
  border: ${({ $hideHeader }) => ($hideHeader ? 'none' : '1px solid #e0e0e0')};
  border-radius: ${({ $hideHeader }) => ($hideHeader ? '0' : '4px')};
  overflow: hidden;
  background: ${({ theme }) => theme.palette.white};
`;

const StyledPreviewFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: ${({ theme }) => theme.palette.white};
`;

const StyledPreviewHeader = styled.div`
  padding: 8px 12px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.textDimmed};
`;

export const BlockPreview: FC<BlockPreviewProps> = ({
  blocks,
  htmlCode,
  cssCode,
  jsCode,
  variables,
  context = {},
  hideHeader = false,
  disableScripts = false,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Register common Handlebars helpers and partials
  useEffect(() => {
    registerHandlebarsHelpers();

    for (const b of blocks) {
      Handlebars.registerPartial(b.name, `${b.html || ''} \n<style>${b.blockStylesheet?.css || ''}</style>`);
    }
  }, []);

  useEffect(() => {
    if (!iframeRef.current) return;

    const iframe = iframeRef.current;

    try {
      // Compile the HTML template with Handlebars
      let compiledHtml = htmlCode;

      if (htmlCode.includes('{{')) {
        const template = Handlebars.compile(htmlCode);

        let parsedVariables: any = {};
        try {
          if (variables) {
            parsedVariables = JSON.parse(variables);
          }
        } catch (error) {
          console.error('Error parsing variables JSON:', error);
        }

        const templateContext = {
          ...parsedVariables.Context,
          ...context,
        };

        compiledHtml = template(templateContext, {
          data: {
            System: parsedVariables.System || {},
            Project: parsedVariables.Project || {},
          },
        });
      }

      // Create the complete HTML document
      const fullHtml = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Block Preview</title>
          <style>
            body {
              margin: 0;
              padding: 16px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              background: white;
            }
            ${cssCode}
          </style>
        </head>
        <body>
          ${compiledHtml}
          ${
            !disableScripts
              ? `<script>
            try {
              ${jsCode}
            } catch (error) {
              console.error('JavaScript execution error:', error);
            }
          </script>`
              : ''
          }
        </body>
        </html>
      `;

      iframe.srcdoc = fullHtml;
    } catch (error) {
      console.error('Error compiling Handlebars template:', error);

      iframe.srcdoc = `<!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Block Preview</title>
          <style>
            body {
              margin: 0;
              padding: 16px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              background: white;
            }
            ${cssCode}
          </style>
        </head>
        <body>
          <div style="color: red; padding: 10px; border: 1px solid red; background: #ffe6e6;">
            Error compiling template: ${error}
          </div>
          ${htmlCode}
        </body>
        </html>`;
    }
  }, [htmlCode, cssCode, jsCode, variables, context, disableScripts]);

  return (
    <StyledPreviewContainer $hideHeader={hideHeader}>
      {!hideHeader && <StyledPreviewHeader>Preview</StyledPreviewHeader>}
      <StyledPreviewFrame
        ref={iframeRef}
        title="Block Preview"
        sandbox={disableScripts ? 'allow-same-origin' : 'allow-scripts allow-same-origin'}
      />
    </StyledPreviewContainer>
  );
};
